/**
 * Tipos de ponta, no espírito do Samsung Notes (esferográfica, tinteiro,
 * lápis, marcador, caligrafia) mais o marca-texto.
 *
 * Os números são parâmetros do perfect-freehand e existem para cada ponta ter
 * personalidade própria: `thinning` diz quanto a pressão muda a espessura,
 * `streamline` quanto o traço é suavizado, `taper` como as pontas afinam.
 */
export type PenKind = 'ballpoint' | 'fountain' | 'pencil' | 'marker' | 'calligraphy';
export type Tool = PenKind | 'highlighter';

export interface PenProfile {
  kind: Tool;
  label: string;
  /** espessuras oferecidas, em unidades lógicas da página */
  sizes: number[];
  defaultSize: number;
  defaultOpacity: number;
  /** o usuário pode mudar a opacidade desta ponta? */
  opacityAdjustable: boolean;
  thinning: number;
  smoothing: number;
  streamline: number;
  taperStart: number;
  taperEnd: number;
  /** ponta chanfrada: a espessura varia com a direção do traço */
  nibAngle?: number;
  /** granulado do lápis */
  grain?: boolean;
  /** desenhado por baixo da tinta, multiplicando */
  behindInk?: boolean;
}

export const PEN_PROFILES: Record<Tool, PenProfile> = {
  ballpoint: {
    kind: 'ballpoint', label: 'Esferográfica',
    sizes: [1.4, 2, 2.8, 4, 6], defaultSize: 2.8,
    defaultOpacity: 1, opacityAdjustable: false,
    thinning: 0.35, smoothing: 0.5, streamline: 0.4, taperStart: 0, taperEnd: 0,
  },
  fountain: {
    kind: 'fountain', label: 'Tinteiro',
    sizes: [1.6, 2.4, 3.4, 5, 7], defaultSize: 3.4,
    defaultOpacity: 1, opacityAdjustable: false,
    thinning: 0.75, smoothing: 0.55, streamline: 0.3, taperStart: 8, taperEnd: 14,
  },
  pencil: {
    kind: 'pencil', label: 'Lápis',
    sizes: [1.4, 2.2, 3.2, 4.6, 6.5], defaultSize: 3.2,
    defaultOpacity: 0.85, opacityAdjustable: true,
    thinning: 0.5, smoothing: 0.4, streamline: 0.25, taperStart: 0, taperEnd: 0,
    grain: true,
  },
  marker: {
    kind: 'marker', label: 'Marcador',
    sizes: [3, 5, 8, 12, 18], defaultSize: 8,
    defaultOpacity: 0.95, opacityAdjustable: true,
    thinning: 0.06, smoothing: 0.6, streamline: 0.5, taperStart: 0, taperEnd: 0,
  },
  calligraphy: {
    kind: 'calligraphy', label: 'Caligrafia',
    sizes: [3, 5, 7, 10, 14], defaultSize: 7,
    defaultOpacity: 1, opacityAdjustable: false,
    thinning: 0.6, smoothing: 0.5, streamline: 0.3, taperStart: 0, taperEnd: 0,
    nibAngle: -Math.PI / 4,
  },
  highlighter: {
    kind: 'highlighter', label: 'Marca-texto',
    sizes: [10, 16, 24, 32], defaultSize: 24,
    defaultOpacity: 0.35, opacityAdjustable: true,
    thinning: 0, smoothing: 0.7, streamline: 0.6, taperStart: 0, taperEnd: 0,
    behindInk: true,
  },
};

export const PEN_ORDER: PenKind[] = ['ballpoint', 'fountain', 'pencil', 'marker', 'calligraphy'];

/** Paleta padrão. As três primeiras são as canetas favoritas da SPEC §7.3. */
export const INK_COLORS = [
  '#15213A', '#1F47C9', '#C0352B',
  '#000000', '#4A5568', '#1F8A4C',
  '#B7791F', '#7B3FA0', '#0E7490',
  '#D14D8B', '#8B5E34', '#FFFFFF',
];

export const HIGHLIGHTER_COLORS = [
  '#FFE066', '#A7F3D0', '#BFDBFE', '#FBCFE8', '#FDBA74', '#DDD6FE',
];

/** Traços antigos foram gravados como 'pen'; hoje isso é a esferográfica. */
export function normalizeTool(tool: string): Tool {
  if (tool === 'pen') return 'ballpoint';
  return (tool in PEN_PROFILES ? tool : 'ballpoint') as Tool;
}

export function profileOf(tool: string): PenProfile {
  return PEN_PROFILES[normalizeTool(tool)];
}

export function isHighlighter(tool: string): boolean {
  return normalizeTool(tool) === 'highlighter';
}
